/* ─── Spanish site (Oct 31, 2026) ───
   Real Spanish pages live under /es. Each one is an entry in src/standalone-pages.js with `lang: "es"` and
   `en: "<the English page's address>"`. Everything here is derived from those entries, so adding a Spanish page
   there is enough to give it its route, its Español / English buttons and its hreflang tags.
   How to write a Spanish page, the glossary and the wording rules: SPANISH.md at the top of the repo. */
import { createContext, useContext } from "react";
import { STANDALONE_PAGES } from "./standalone-pages";
import { LIVE_PROVIDERS, providerPath, providerEsPath } from "./providers";

export const LangContext = createContext("en");
export const useLang = () => useContext(LangContext);

export const ES_HOME = "/es";
export const isEsPath = (p) => p === ES_HOME || (typeof p === "string" && p.startsWith(`${ES_HOME}/`));

/* English address → Spanish address, and back. */
export const ES_FOR_EN = {};
export const EN_FOR_ES = {};
Object.entries(STANDALONE_PAGES).forEach(([path, entry]) => {
  if (entry.lang !== "es") return;
  ES_FOR_EN[entry.en] = path;
  EN_FOR_ES[path] = entry.en;
});
/* Provider pages (src/providers.js): a provider with an `es` entry has a Spanish page at /es/equipo/<slug>. */
LIVE_PROVIDERS.filter((p) => p.es).forEach((p) => {
  ES_FOR_EN[providerPath(p)] = providerEsPath(p);
  EN_FOR_ES[providerEsPath(p)] = providerPath(p);
});

/* A link from a Spanish page: the Spanish version of `enPath` when there is one, otherwise the English page.
   Keeps any #section on the end. */
export function esHref(enPath) {
  const [p, hash] = String(enPath).split("#");
  const target = ES_FOR_EN[p || "/"] || p || "/";
  return hash ? `${target}#${hash}` : target;
}

/* Where the language button goes from `path`: the matching page in the other language. An English page with no
   Spanish version yet goes to the Spanish homepage. */
export function otherLanguageHref(path) {
  if (isEsPath(path)) return EN_FOR_ES[path] || "/";
  return ES_FOR_EN[path] || ES_HOME;
}

/* hreflang alternates for a page that exists in both languages; empty otherwise. */
export function alternatesFor(path) {
  const en = isEsPath(path) ? EN_FOR_ES[path] : ES_FOR_EN[path] ? path : null;
  const es = en ? ES_FOR_EN[en] : null;
  if (!en || !es) return [];
  return [
    { hreflang: "en", path: en },
    { hreflang: "es", path: es },
    { hreflang: "x-default", path: en },
  ];
}

/* ── The build check (scripts/prerender.js) ──
   Every English page must have a Spanish version, unless it is listed below. A new page never goes on either
   list: it ships with its Spanish version (SPANISH.md). */

/* English-only on purpose. Legal and policy text, and the blog (posts come from Sanity). Translating these is
   Steven's call, not a default. */
export const ES_EXEMPT = ["/blog", "/privacy", "/hipaa", "/terms", "/accessibility", "/no-surprises-act"];
export const ES_EXEMPT_PREFIXES = ["/blog/"];

/* Pages that existed before the Spanish site and had not been translated yet. Empty since Oct 31, 2026: every page
   has its Spanish version. Nothing is ever added here; a new page ships with its Spanish version (SPANISH.md). */
export const ES_PENDING = [
];

/* Returns a list of problems (empty when all is well). `paths` is every page the build is about to save. */
export function spanishCoverageProblems(paths) {
  const problems = [];
  const all = new Set(paths);
  paths.forEach((p) => {
    if (isEsPath(p)) {
      if (!EN_FOR_ES[p]) problems.push(`${p} is a Spanish page with no \`en\` (English page) in src/standalone-pages.js`);
      else if (!all.has(EN_FOR_ES[p])) problems.push(`${p} says its English page is ${EN_FOR_ES[p]}, which does not exist`);
      return;
    }
    if (ES_FOR_EN[p]) {
      if (ES_PENDING.includes(p)) problems.push(`${p} now has a Spanish version (${ES_FOR_EN[p]}); remove it from ES_PENDING in src/i18n.js`);
      return;
    }
    if (ES_EXEMPT.includes(p) || ES_EXEMPT_PREFIXES.some((pre) => p.startsWith(pre)) || ES_PENDING.includes(p)) return;
    problems.push(`${p} has no Spanish version. Every new page ships with one (SPANISH.md): add src/pages/es/<Name>.js and its entry in src/standalone-pages.js`);
  });
  ES_PENDING.forEach((p) => { if (!all.has(p)) problems.push(`${p} is in ES_PENDING in src/i18n.js but is not a page any more; remove it`); });
  return problems;
}

/* ── Words used by the shared navigation, footer and buttons ── */
export const UI = {
  en: {
    switchTo: "Español", switchToLang: "es", switchToLabel: "Ver esta página en español",
    skipToMain: "Skip to main content",
    contrast: ["High contrast", "Standard contrast"], contrastSkip: ["Turn on high contrast", "Turn off high contrast"],
  },
  es: {
    switchTo: "English", switchToLang: "en", switchToLabel: "View this page in English",
    skipToMain: "Saltar al contenido principal",
    contrast: ["Alto contraste", "Contraste estándar"], contrastSkip: ["Activar alto contraste", "Desactivar alto contraste"],
    nav: { home: "Inicio", about: "Nosotros", procedures: "Procedimientos", gallery: "Galería", resources: "Información", locations: "Ubicaciones", contact: "Contacto", blog: "Blog" },
    portal: ["Portal del", "paciente"], portalShort: "Portal del paciente",
    callUs: "Llámenos al (212) 222-4628",
    openMenu: "Abrir menú", closeMenu: "Cerrar menú",
    showList: (what) => `Mostrar la lista de ${what}`, hideList: (what) => `Ocultar la lista de ${what}`,
    listWhat: { procedures: "procedimientos", people: "profesionales" },
    footerAbout: "Cirujano plástico certificado, con atención personalizada en Nueva York y Nueva Jersey.",
    quickLinks: "Enlaces rápidos", proceduresHeading: "Procedimientos", contactHeading: "Contacto",
    /* [label, English address]; each goes through esHref, so the Spanish homepage keeps the English section ids. */
    quick: [["Sobre el Dr. Ovadia", "/about-dr-ovadia"], ["Procedimientos", "/#procedures"], ["Galería", "/#gallery"], ["Información para pacientes", "/#resources"], ["Contacto", "/#contact"]],
    moreProcedures: "Todos los procedimientos",
    legal: { privacy: "Privacidad (en inglés)", terms: "Términos de uso (en inglés)", accessibility: "Accesibilidad (en inglés)" },
    emergency: "Si tiene una emergencia médica, llame al 911 o acuda a la sala de emergencias más cercana.",
    rights: "Todos los derechos reservados. Los resultados individuales pueden variar.",
    abpsAlt: "Certificado por The American Board of Plastic Surgery", aspsAlt: "Miembro de la American Society of Plastic Surgeons",
    pageFailed: "No se pudo cargar esta página.", tryAgain: "Intentar de nuevo",
    categories: {
      "breast-reconstruction": "Reconstrucción mamaria",
      face: "Rostro",
      craniofacial: "Craneofacial",
      breast: "Senos",
      body: "Cuerpo",
      "gender-affirming": "Afirmación de género",
      additional: "Procedimientos adicionales",
      "non-surgical": "Tratamientos no quirúrgicos",
    },
  },
};
